import {
  FormControl,
  FormControlProps,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export type Props = {
  onDeviceChange(deviceId: string | undefined): void;
} & FormControlProps;

type DeviceInfo = {
  deviceId: string;
  label: string;
};

export default function CameraSelector({ onDeviceChange, ...props }: Props) {
  const { t } = useTranslation();
  const [deviceId, setDeviceId] = useState<string | undefined>();
  const [devices, setDevices] = useState<Array<DeviceInfo>>([]);

  const selectDeviceId = useCallback(
    (id: string) => {
      window.localStorage.setItem("rescuetablet:cameraId", id);
      setDeviceId(id);
      onDeviceChange(id);
    },
    [onDeviceChange],
  );

  const updateDevices = useCallback(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoDevices = devices.filter((d) => d.kind === "videoinput");
      setDevices(videoDevices);

      const storedDeviceId =
        window.localStorage.getItem("rescuetablet:cameraId") || undefined;
      if (
        storedDeviceId &&
        videoDevices.some((d) => d.deviceId === storedDeviceId)
      ) {
        selectDeviceId(storedDeviceId);
      } else if (videoDevices.length > 0) {
        selectDeviceId(videoDevices[0].deviceId);
      }
    });
  }, [selectDeviceId]);

  useEffect(() => {
    updateDevices();
    navigator.mediaDevices.addEventListener("devicechange", updateDevices);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", updateDevices);
    };
  }, [updateDevices]);

  if (devices.length < 2) return null;

  return (
    <FormControl {...props}>
      <InputLabel id="camera">{t("camera")}</InputLabel>
      <Select
        labelId="camera"
        label={t("camera")}
        value={deviceId ?? ""}
        onChange={(e) => selectDeviceId(e.target.value)}
      >
        {devices.map((device, i) => (
          <MenuItem key={device.deviceId} value={device.deviceId}>
            {device.label || `Camera #${i + 1}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
